/**
 * Message Builder
 *
 * Prepares and cleans messages for LLM calls, including:
 * - Image injection (base64 images in user messages for vision analysis)
 * - Context resolution (short IDs to full content)
 * - Message formatting
 * - Reminder injection and stripping (via MessagePreparer)
 *
 * ## Image Return Pattern
 *
 * Images cannot be in tool results (LLM needs to see them as user input for vision).
 * The pattern is:
 * 1. Tool returns image metadata (short ID, dimensions, etc.)
 * 2. MessageBuilder injects the actual image in the next user message
 * 3. LLM performs vision analysis on the image
 *
 * ```typescript
 * // After tool returns { assetId: "logo_abc12", ... }
 * const nextMessage = messageBuilder.buildNextMessage(
 *     "Please analyze this logo and describe its design",
 *     ["logo_abc12"]  // Images to inject
 * );
 * // nextMessage.content = [
 * //   { type: "text", text: "Please analyze..." },
 * //   { type: "image_url", image_url: { url: "data:image/png;base64,..." } }
 * // ]
 * ```
 *
 * ## Reminder Pattern
 *
 * Non-user automated reminders are contextual information injected into
 * user messages that the AI should use but NOT acknowledge to the user.
 *
 * ```typescript
 * const message = await messageBuilder.buildUserMessageWithReminders(
 *     "Create a slide about Q3 revenue",
 *     { includePresentation: true, includeSecurityWarning: true }
 * );
 * // message.content includes:
 * // - User's text
 * // - <non_user_automated_reminder>...</non_user_automated_reminder> tags
 * ```
 */
import { getAgentServices } from '../services';
import { estimateImageTokens, getTokensFromMetadata } from '../utils/imageUtils';
// ═══════════════════════════════════════════════════════════════════════════
// MESSAGE BUILDER CLASS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Builds and prepares messages for LLM calls
 */
export class MessageBuilder {
    constructor() {
        /** Images pending injection in the next user message */
        this.pendingImages = [];
        /** Feedback messages pending injection after tool results */
        this.pendingFeedbackMessages = [];
    }
    getContextResolver() {
        return getAgentServices().contextResolver;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // IMAGE MANAGEMENT
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Queue an image for injection in the next user message
     *
     * @param id - Short ID of the image (logo_*, wikimedia_*, etc.)
     * @param caption - Optional caption to include with the image
     */
    queueImage(id, caption) {
        this.pendingImages.push({ id, caption });
    }
    /**
     * Queue multiple images
     */
    queueImages(ids, captions) {
        ids.forEach((id, i) => {
            this.queueImage(id, captions?.[i]);
        });
    }
    /**
     * Clear pending images
     */
    clearPendingImages() {
        this.pendingImages = [];
    }
    /**
     * Get count of pending images
     */
    getPendingImageCount() {
        return this.pendingImages.length;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // FEEDBACK MESSAGE MANAGEMENT
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Queue a feedback message for injection after tool results.
     *
     * This is used by ExecutionAgent to inject slide state feedback
     * after buildArea calls. The message should include reminder tags
     * and the REMINDER_IMAGE_MARKER if it contains an associated image.
     *
     * @param message - The ChatMessage to inject
     * @param source - Debug label for logging
     */
    queueFeedbackMessage(message, source = 'unknown') {
        this.pendingFeedbackMessages.push({ message, source });
        console.log(`[MessageBuilder] 📝 Queued feedback message from ${source}`);
    }
    /**
     * Get and clear all pending feedback messages
     *
     * @returns Array of pending feedback messages (cleared after return)
     */
    getPendingFeedbackMessages() {
        const messages = [...this.pendingFeedbackMessages];
        this.pendingFeedbackMessages = [];
        return messages;
    }
    /**
     * Check if there are pending feedback messages
     */
    hasPendingFeedbackMessages() {
        return this.pendingFeedbackMessages.length > 0;
    }
    /**
     * Clear all pending feedback messages without returning them
     */
    clearPendingFeedbackMessages() {
        this.pendingFeedbackMessages = [];
    }
    // ═══════════════════════════════════════════════════════════════════════
    // MESSAGE BUILDING
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Build a user message with optional image injection
     *
     * This is the main method for creating user messages that may include images.
     * Any pending images are injected and cleared.
     *
     * @param text - The text content of the message
     * @param additionalImages - Optional additional image IDs to inject (beyond pending)
     * @returns ChatMessage ready for LLM call
     */
    buildUserMessage(text, additionalImages) {
        const allImages = [
            ...this.pendingImages,
            ...(additionalImages?.map(id => ({ id, caption: undefined })) || [])
        ];
        // Clear pending images after use
        this.pendingImages = [];
        // If no images, return simple text message
        if (allImages.length === 0) {
            return {
                role: 'user',
                content: text,
            };
        }
        // Build multimodal content
        const content = [];
        // Add text first
        content.push({
            type: 'text',
            text: text,
        });
        // Add images
        const resolver = this.getContextResolver();
        for (const img of allImages) {
            const imageData = resolver?.getImage ? resolver.getImage(img.id) : null;
            if (imageData) {
                // Add caption if present
                if (img.caption) {
                    content.push({
                        type: 'text',
                        text: `[Image: ${img.caption}]`,
                    });
                }
                // Add the image
                content.push({
                    type: 'image_url',
                    image_url: {
                        url: `data:${imageData.mimeType};base64,${imageData.base64}`,
                        detail: 'auto',
                    },
                });
            }
            else {
                // Image not found - add placeholder text
                content.push({
                    type: 'text',
                    text: `[Image ${img.id} not found]`,
                });
                if (!resolver?.getImage) {
                    console.warn(`[MessageBuilder] contextResolver.getImage unavailable; cannot inject image ${img.id}`);
                }
                else {
                    console.warn(`[MessageBuilder] Image not found: ${img.id}`);
                }
            }
        }
        return {
            role: 'user',
            content,
        };
    }
    /**
     * Build an assistant message
     */
    buildAssistantMessage(text) {
        return {
            role: 'assistant',
            content: text,
        };
    }
    /**
     * Build a tool result message
     *
     * Note: Images should NOT be included in tool results.
     * Queue them for the next user message instead.
     *
     * Strips internal fields (_imageIds, _logImages) that are used for
     * injection/debugging but shouldn't be sent to the LLM.
     */
    buildToolResultMessage(toolCallId, result, imageIds) {
        // Queue any images for injection in next user message
        if (imageIds && imageIds.length > 0) {
            this.queueImages(imageIds);
        }
        // Strip internal fields before sending to LLM
        let cleanedResult = result;
        let slideInfos;
        let excelReadInfos;
        if (typeof result === 'object' && result !== null) {
            const { _imageIds, _logImages, _slideInfos, _excelReadInfos, ...rest } = result;
            cleanedResult = rest;
            slideInfos = _slideInfos;
            excelReadInfos = _excelReadInfos;
        }
        const message = {
            role: 'tool',
            tool_call_id: toolCallId,
            content: typeof cleanedResult === 'string' ? cleanedResult : JSON.stringify(cleanedResult),
        };
        if (Array.isArray(slideInfos)) {
            message._slideInfos = slideInfos;
        }
        if (Array.isArray(excelReadInfos)) {
            message._excelReadInfos = excelReadInfos;
        }
        return message;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // CONTEXT RESOLUTION
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Build a message with resolved context
     *
     * Replaces short IDs with their actual content.
     * For slide content, uses LLM-friendly text format if available (_textFormat property).
     *
     * @param text - Text that may contain short ID references
     * @param contextIds - Short IDs to resolve and include
     * @returns Message with resolved context
     */
    buildMessageWithContext(text, contextIds) {
        const resolver = this.getContextResolver();
        if (!resolver?.resolve) {
            console.warn('[MessageBuilder] contextResolver.resolve unavailable; skipping context expansion');
            return this.buildUserMessage(text);
        }
        const resolvedContent = [];
        for (const id of contextIds) {
            const resolved = resolver.resolve(id);
            if (resolved) {
                // Use LLM-friendly text format if available (for slides)
                // This provides better readability than raw JSON
                const content = resolved.content;
                let formattedContent;
                if (content && typeof content === 'object' && '_textFormat' in content) {
                    // Use pre-formatted text for LLM consumption
                    formattedContent = content._textFormat;
                }
                else {
                    // Fall back to JSON for non-slide content or legacy data
                    formattedContent = JSON.stringify(resolved.content, null, 2);
                }
                resolvedContent.push(`--- Context: ${id} ---\n${formattedContent}`);
            }
        }
        const fullText = resolvedContent.length > 0
            ? `${text}\n\n${resolvedContent.join('\n\n')}`
            : text;
        return this.buildUserMessage(fullText);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // REMINDER INTEGRATION
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Build a user message with automated reminders
     *
     * Collects contextual information and injects it as non-user reminders.
     * The AI should use this context but NOT acknowledge it to the user.
     *
     * Reminders are added as separate content parts in the content array,
     * following the Python pattern for easy stripping later.
     *
     * @param text - The user's message text
     * @param options - What context to include
     * @returns ChatMessage with reminders injected as content array
     */
    async buildUserMessageWithReminders(text, options = {}) {
        const preparer = getAgentServices().messagePreparer;
        const collector = getAgentServices().reminderCollector;
        // Collect context
        const context = await collector.collectContext({
            includePresentation: options.includePresentation,
            includeCustomInstructions: options.includeCustomInstructions,
            includeSecurityWarning: options.includeSecurityWarning,
            includeDeckRegistry: options.includeDeckRegistry,
            externalContext: options.externalContext,
        });
        // Add reminders to text - now returns ContentPart[]
        const contentWithReminders = preparer.addRemindersToMessage(text, context, 'MessageBuilder');
        // Handle any pending/additional images
        const allImages = [
            ...this.pendingImages,
            ...(options.additionalImages?.map(id => ({ id, caption: undefined })) || [])
        ];
        // Clear pending images after use
        this.pendingImages = [];
        // If no images, return message with content array directly
        if (allImages.length === 0) {
            return {
                role: 'user',
                content: contentWithReminders,
            };
        }
        // Merge content with images
        const finalContent = [...contentWithReminders];
        const resolver = this.getContextResolver();
        for (const img of allImages) {
            const imageData = resolver?.getImage ? resolver.getImage(img.id) : null;
            if (imageData) {
                if (img.caption) {
                    finalContent.push({
                        type: 'text',
                        text: `[Image: ${img.caption}]`,
                    });
                }
                finalContent.push({
                    type: 'image_url',
                    image_url: {
                        url: `data:${imageData.mimeType};base64,${imageData.base64}`,
                        detail: 'auto',
                    },
                });
            }
            else {
                finalContent.push({
                    type: 'text',
                    text: `[Image ${img.id} not found]`,
                });
                if (!resolver?.getImage) {
                    console.warn(`[MessageBuilder] contextResolver.getImage unavailable; cannot inject image ${img.id}`);
                }
                else {
                    console.warn(`[MessageBuilder] Image not found: ${img.id}`);
                }
            }
        }
        return {
            role: 'user',
            content: finalContent,
        };
    }
    /**
     * Prepare a message array for LLM by stripping old reminders
     * and optionally adding fresh context to the last message.
     *
     * @param messages - Full message history
     * @param freshContext - Optional fresh context for the last message
     * @returns Cleaned messages ready for LLM
     */
    prepareMessagesForLlm(messages, freshContext) {
        const preparer = getAgentServices().messagePreparer;
        return preparer.prepareMessagesForLlm(messages, freshContext);
    }
    /**
     * Strip reminders from a single message
     */
    stripRemindersFromMessage(message) {
        const preparer = getAgentServices().messagePreparer;
        if (typeof message.content === 'string') {
            return {
                ...message,
                content: preparer.stripReminders(message.content),
            };
        }
        if (Array.isArray(message.content)) {
            return {
                ...message,
                content: message.content.map(part => {
                    if (part.type === 'text') {
                        return {
                            ...part,
                            text: preparer.stripReminders(part.text),
                        };
                    }
                    return part;
                }),
            };
        }
        return message;
    }
    /**
     * Check if a message contains reminders
     */
    messageHasReminders(message) {
        const preparer = getAgentServices().messagePreparer;
        if (typeof message.content === 'string') {
            return preparer.hasReminders(message.content);
        }
        return preparer.hasReminders(message.content);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // MESSAGE CLEANING
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Clean a message array for sending to LLM
     *
     * Removes empty messages, normalizes content, etc.
     */
    cleanMessages(messages) {
        return messages.filter(msg => {
            // Remove messages with no content
            if (!msg.content && msg.role !== 'assistant') {
                return false;
            }
            // Keep tool messages even if content is empty (they have tool_call_id)
            if (msg.role === 'tool') {
                return true;
            }
            return true;
        });
    }
    /**
     * Truncate messages to fit within token limit (rough estimate)
     *
     * @param messages - Messages to truncate
     * @param maxTokens - Approximate max tokens
     * @param keepSystemMessage - Whether to always keep the first message if it's system
     */
    truncateMessages(messages, maxTokens, keepSystemMessage = true) {
        let totalTokens = 0;
        const result = [];
        // Process in reverse to keep most recent messages
        const reversed = [...messages].reverse();
        for (const msg of reversed) {
            const msgTokens = this.estimateTokens(msg);
            if (totalTokens + msgTokens > maxTokens) {
                // Check if this is the system message we should keep
                if (keepSystemMessage && msg === messages[0] && msg.role === 'system') {
                    result.unshift(msg);
                }
                break;
            }
            totalTokens += msgTokens;
            result.unshift(msg);
        }
        return result;
    }
    /**
     * Estimate tokens in a message (rough approximation)
     *
     * Uses Claude's token calculation for images: (width × height) / 750
     * Falls back to default estimate if image dimensions unknown.
     */
    estimateTokens(message) {
        let textTokens = 0;
        let imageTokens = 0;
        if (typeof message.content === 'string') {
            textTokens = Math.ceil(message.content.length / 4);
        }
        else if (Array.isArray(message.content)) {
            // Text content
            const textContent = message.content
                .filter((part) => part.type === 'text')
                .map(part => part.text)
                .join(' ');
            textTokens = Math.ceil(textContent.length / 4);
            // Image content - use proper token estimation
            for (const part of message.content) {
                if (part.type === 'image_url') {
                    // Try to get dimensions from URL or use default
                    const imageData = this.extractImageIdFromUrl(part.image_url?.url);
                    if (imageData) {
                        const resolver = this.getContextResolver();
                        const cached = resolver?.getImage ? resolver.getImage(imageData) : null;
                        if (cached?.metadata) {
                            imageTokens += getTokensFromMetadata(cached.metadata);
                        }
                        else {
                            // Default for unknown dimensions (1024x768)
                            imageTokens += estimateImageTokens(1024, 768);
                        }
                    }
                    else {
                        // Default for unknown images
                        imageTokens += estimateImageTokens(1024, 768);
                    }
                }
            }
        }
        // Add overhead for message structure
        return textTokens + imageTokens + 10;
    }
    /**
     * Extract image ID from a data URL if it was cached
     *
     * Note: This is a best-effort approach - we can't always determine
     * the original ID from a data URL.
     */
    extractImageIdFromUrl(url) {
        if (!url)
            return null;
        // If it's a data URL, we can't easily determine the original ID
        // The contextResolver would need to track this separately
        // For now, return null and use default estimation
        return null;
    }
    /**
     * Estimate tokens for a specific image by ID
     */
    estimateImageTokensById(imageId) {
        const resolver = this.getContextResolver();
        const imageData = resolver?.getImage ? resolver.getImage(imageId) : null;
        if (imageData?.metadata) {
            return getTokensFromMetadata(imageData.metadata);
        }
        // Default for unknown dimensions
        return estimateImageTokens(1024, 768);
    }
    /**
     * Get total estimated tokens for all pending images
     */
    getPendingImageTokens() {
        let total = 0;
        for (const img of this.pendingImages) {
            total += this.estimateImageTokensById(img.id);
        }
        return total;
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// IMAGE ID EXTRACTION
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Extract image IDs from a tool result
 *
 * Supports multiple conventions:
 * - `_imageIds: string[]` - Explicit array of image IDs (preferred)
 * - `assetId: string` - Single asset ID
 * - `imageId: string` - Single image ID
 * - `images: Array<{ id: string }>` - Array of image objects
 *
 * @param result - Tool result to extract image IDs from
 * @returns Array of image IDs found
 */
export function extractImageIds(result) {
    if (typeof result !== 'object' || result === null) {
        return [];
    }
    const obj = result;
    const imageIds = [];
    // Check for _imageIds convention (preferred)
    if (Array.isArray(obj._imageIds)) {
        for (const id of obj._imageIds) {
            if (typeof id === 'string') {
                imageIds.push(id);
            }
        }
    }
    // Check for single assetId
    if (typeof obj.assetId === 'string' && !imageIds.includes(obj.assetId)) {
        imageIds.push(obj.assetId);
    }
    // Check for single imageId
    if (typeof obj.imageId === 'string' && !imageIds.includes(obj.imageId)) {
        imageIds.push(obj.imageId);
    }
    // Check for images array with id property
    if (Array.isArray(obj.images)) {
        for (const img of obj.images) {
            if (typeof img === 'object' && img !== null) {
                const imgObj = img;
                if (typeof imgObj.id === 'string' && !imageIds.includes(imgObj.id)) {
                    imageIds.push(imgObj.id);
                }
            }
        }
    }
    return imageIds;
}
/**
 * Check if a tool result contains images
 */
export function hasImages(result) {
    return extractImageIds(result).length > 0;
}
// ═══════════════════════════════════════════════════════════════════════════
// SINGLETON
// ═══════════════════════════════════════════════════════════════════════════
let messageBuilderInstance = null;
/**
 * Get the MessageBuilder singleton
 */
export function getMessageBuilder() {
    if (!messageBuilderInstance) {
        messageBuilderInstance = new MessageBuilder();
    }
    return messageBuilderInstance;
}
export default MessageBuilder;
